import { type FieldMetadata, useInputControl } from "@conform-to/react";
import {
  Switch as HeadlessSwitch,
  type SwitchProps as HeadlessSwitchProps,
} from "@headlessui/react";
import type { VariantProps } from "class-variance-authority";
import { classNames } from "../../utils/classNames";
import { switchVariants } from "./variants";

type SwitchProps = VariantProps<typeof switchVariants> &
  HeadlessSwitchProps & {
    meta?: FieldMetadata<string>;
  };

export function Switch({
  color = "dark/zinc",
  className,
  children,
  meta,
  onChange,
  ...props
}: SwitchProps) {
  const control = meta
    ? // biome-ignore lint/correctness/useHookAtTopLevel: If 'meta' is not provided then 'useInputControl' should not be called at all
      useInputControl(meta)
    : {
        blur: undefined,
        focus: undefined,
        change: undefined,
        value: undefined,
      };

  return (
    <HeadlessSwitch
      data-slot="control"
      className={switchVariants({ color, className })}
      onChange={(checked) => {
        control.change?.(checked ? "on" : "off");
        onChange?.(checked);
      }}
      defaultValue={control.value}
      defaultChecked={control.value === "on"}
      {...props}
    >
      <span
        aria-hidden="true"
        className={classNames(
          // Basic layout
          "pointer-events-none relative inline-block size-[1.125rem] rounded-full sm:size-3.5",

          // Transition
          "translate-x-0 transition duration-200 ease-in-out",

          // Invisible border so the switch is still visible in forced-colors mode
          "border border-transparent",

          // Unchecked
          "bg-white shadow ring-1 ring-black/5",

          // Checked
          "group-data-[checked]/switch:bg-[--switch] group-data-[checked]/switch:shadow-[--switch-shadow] group-data-[checked]/switch:ring-[--switch-ring]",
          "group-data-[checked]/switch:translate-x-4 sm:group-data-[checked]/switch:translate-x-3",

          // Disabled
          "group-data-[disabled]/switch:group-data-[checked]/switch:bg-white group-data-[disabled]:group-data-[checked]/switch:shadow group-data-[disabled]/switch:group-data-[checked]/switch:ring-black/5",
        )}
      />
    </HeadlessSwitch>
  );
}
